import { Link } from 'react-router-dom';
import { ArrowRight, Target, Users, TrendingUp, Music } from 'lucide-react';

const services = [
  {
    icon: Music,
    title: 'Distribution',
    description: 'Global music distribution to all major platforms and streaming services.',
  },
  {
    icon: Users,
    title: 'Artist Management',
    description: 'Strategic career guidance and professional artist representation.',
  },
  {
    icon: Target,
    title: 'Licensing',
    description: 'Comprehensive music licensing solutions for films, TV, and commercials.',
  },
  {
    icon: TrendingUp,
    title: 'Market Expansion',
    description: 'Strategic planning for international market penetration and growth.',
  },
];

const HomePage = () => {
  return (
    <div className="page-transition" data-testid="home-page">
      {/* Hero Section - Artist in Recording Studio */}
      <section 
        className="hero-bg min-h-[90vh] flex items-center relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1561446289-4112a4f79116?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="hero-section"
      >
        <div className="hero-content container-custom">
          <div className="max-w-3xl">
            <div className="gold-line mb-8 opacity-0 animate-fade-in" />
            <h1 
              className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-tight opacity-0 animate-fade-in animation-delay-200"
              data-testid="hero-title"
            >
              Eagle's Vision.<br />
              <span style={{ color: '#D4AF37' }}>Excellence Delivered.</span>
            </h1>
            <p 
              className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed opacity-0 animate-fade-in animation-delay-400"
              data-testid="hero-subtitle"
            >
              Empowering the music industry with strategic consultancy, 
              innovative solutions, and unparalleled expertise in distribution, 
              licensing, and artist development.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in animation-delay-600">
              <Link 
                to="/services" 
                className="btn-primary inline-flex items-center justify-center gap-2"
                data-testid="hero-cta-services"
              >
                Explore Services
                <ArrowRight size={18} />
              </Link>
              <Link 
                to="/contact" 
                className="btn-secondary inline-flex items-center justify-center"
                data-testid="hero-cta-contact"
              >
                Get in Touch
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Preview Section - Music Producer at Work */}
      <section className="section-padding bg-richblack-light" data-testid="about-preview-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="gold-line mb-6" />
              <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
                Transforming the Music Industry
              </h2>
              <p className="text-white/60 leading-relaxed mb-6">
                EAGLENCE stands at the forefront of music industry consultancy, 
                combining decades of experience with innovative strategies. We partner 
                with artists, labels, and businesses to navigate the complex landscape 
                of modern music distribution and management.
              </p>
              <p className="text-white/60 leading-relaxed mb-8">
                Our name embodies our philosophy: the vision of an eagle combined with 
                a relentless pursuit of excellence. We see opportunities others miss 
                and execute with precision.
              </p>
              <Link 
                to="/services" 
                className="text-gold hover:text-gold-light inline-flex items-center gap-2 transition-colors"
                data-testid="about-preview-link"
              >
                Explore Our Services
                <ArrowRight size={16} />
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1658247060442-9cc9b2e2a3f9?w=800&q=80"
                alt="Music Producer Working in Studio"
                className="w-full h-[400px] object-cover hover:scale-[1.02] transition-transform duration-700"
              />
              <div className="absolute inset-0 border border-gold/20 -translate-x-4 -translate-y-4 pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* Music Gallery Section */}
      <section className="py-4 bg-richblack">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <img 
              src="https://images.unsplash.com/photo-1543510616-a4025b9c1434?w=400&q=80" 
              alt="Singer Recording" 
              className="w-full h-32 md:h-48 object-cover opacity-60 hover:opacity-100 transition-opacity duration-500"
            />
            <img 
              src="https://images.unsplash.com/photo-1693454992816-f2bcbcd1cefb?w=400&q=80" 
              alt="Studio Headphones" 
              className="w-full h-32 md:h-48 object-cover opacity-60 hover:opacity-100 transition-opacity duration-500"
            />
            <img 
              src="https://images.unsplash.com/photo-1610716632061-f76783ec35c9?w=400&q=80" 
              alt="Music Production" 
              className="w-full h-32 md:h-48 object-cover opacity-60 hover:opacity-100 transition-opacity duration-500"
            />
            <img 
              src="https://images.unsplash.com/photo-1579870946215-8284f1a47c9a?w=400&q=80" 
              alt="Studio Microphone" 
              className="w-full h-32 md:h-48 object-cover opacity-60 hover:opacity-100 transition-opacity duration-500"
            />
          </div>
        </div>
      </section>

      {/* Services Preview Section */}
      <section className="section-padding bg-richblack" data-testid="services-preview-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <div className="gold-line mx-auto mb-6" />
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
              Our Expertise
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Comprehensive consultancy services tailored for the modern music industry
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div 
                key={service.title}
                className="service-card group"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`service-card-${index}`}
              >
                <div>
                  <service.icon className="text-gold mb-6" size={32} strokeWidth={1.5} />
                  <h3 className="font-serif text-xl text-white mb-3">{service.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed">{service.description}</p>
                </div>
                <div className="mt-6 pt-6 border-t border-white/5">
                  <Link 
                    to="/services" 
                    className="text-gold text-sm hover:text-gold-light inline-flex items-center gap-2 transition-colors"
                  >
                    Learn More
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/services" 
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="view-all-services-btn"
            >
              View All Services
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section - Artist Singing */}
      <section 
        className="section-padding relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1601814837661-ce2832acd413?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="cta-section"
      >
        <div className="absolute inset-0 bg-richblack/85" />
        <div className="container-custom relative z-10 text-center">
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white mb-6">
            Ready to Elevate Your Music Business?
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto mb-10">
            Let's discuss how EAGLENCE can help you achieve your goals in the music industry.
          </p>
          <Link 
            to="/contact" 
            className="btn-primary inline-flex items-center gap-2"
            data-testid="cta-contact-btn"
          >
            Start a Conversation
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
